/**
 * Guide Admin Editor — mirrors RulesEditor.jsx.
 *
 * Layout:
 *   - Sticky save/reset bar (only shows the Save button when dirty)
 *   - Meta panel (maintained_by)
 *   - Server connection: IP + port only
 *   - Sortable list of sections (drag handle + auto-renumber)
 *   - Inside each section: sortable subsections
 *   - Inside each subsection: an ordered list of content blocks
 *     (paragraph / steps / code / image / callout / table). Each block gets
 *     its own tiny editor.
 *
 * DnD parity with the rules editor: keyboard-accessible, DragOverlay ghost
 * card, auto-renumber on drop/delete for top-level sections.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, XCircle, RotateCcw, Trash2, Pencil, Plus, Save, GripVertical, Book, Upload,
} from "lucide-react";
import {
  DndContext, DragOverlay, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, MeasuringStrategy,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable,
  sortableKeyboardCoordinates, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api";
import { GUIDE_ICON_MAP } from "@/components/site/Guide";
import { TextField, TextArea, MarkdownTextArea, IconPicker, IconBtn, HexColorRow } from "./adminPrimitives";

let __uid = 0;
function uid() { __uid += 1; return `g-${Date.now().toString(36)}-${__uid}`; }

// -------- Entry point --------

export function GuideSection({ isSuperAdmin }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);
  const [icons, setIcons] = useState([]);
  const [colors, setColors] = useState([]);
  const [blockTypes, setBlockTypes] = useState([]);
  const [calloutKinds, setCalloutKinds] = useState([]);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/content/guide");
      setContent(res.data?.content || null);
      setIcons(res.data?.available_icons || []);
      setColors(res.data?.available_colors || []);
      setBlockTypes(res.data?.available_block_types || []);
      setCalloutKinds(res.data?.available_callout_kinds || []);
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load guide.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isSuperAdmin) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-5 text-[#8D9A87] text-sm">
        <span className="font-mono-ui">// Only the server owner can edit the Guide page.</span>
      </div>
    );
  }
  if (loading || !content) {
    return (
      <div className="text-center py-16">
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={24} />
      </div>
    );
  }

  const update = (patch) => { setContent((c) => ({ ...c, ...patch })); setDirty(true); };
  const updateServer = (patch) => update({ server_info: { ...content.server_info, ...patch } });
  const updateSections = (fn) => update({ sections: fn(content.sections) });

  const addSection = () => {
    const next = [...content.sections];
    const num = String(next.length + 1).padStart(2, "0");
    next.push({
      _uid: uid(),
      id: `section-${Date.now()}`,
      number: num,
      title: "New Section",
      icon: "Book",
      color: colors[0] || "#C0A875",
      subsections: [{ _uid: uid(), title: "Overview", blocks: [] }],
    });
    updateSections(() => next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/content/guide", content);
      setContent(res.data?.content || content);
      setDirty(false);
      toast.success("Guide saved. Reload /guide to see it live.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the guide to the built-in defaults?")) return;
    setSaving(true);
    try {
      const res = await api.post("/admin/content/guide/reset");
      setContent(res.data?.content);
      setDirty(false);
      toast.success("Guide reset.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="guide-editor">
      {/* Sticky bar */}
      <div className="sticky top-16 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-[#0D110C]/95 backdrop-blur-md border-b border-[#283524] flex items-center justify-between gap-3">
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
          {dirty ? <span className="text-[#C0A875]">// UNSAVED CHANGES</span> : <span>// UP TO DATE</span>}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={reset} disabled={saving} data-testid="guide-reset"
            className="inline-flex items-center gap-2 border border-[#5C6B51] text-[#8D9A87] hover:text-[#E2E4DF] px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] disabled:opacity-50">
            <RotateCcw size={12} /> Reset defaults
          </button>
          <button type="button" onClick={save} disabled={saving || !dirty} data-testid="guide-save"
            className="inline-flex items-center gap-2 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] font-semibold">
            {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Meta */}
      <div className="bg-[#161D14] border border-[#283524] p-5">
        <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Meta</h4>
        <div className="mt-4">
          <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
            Last updated <span className="text-[#5C6B51]">(auto-set on save)</span>
          </label>
          <input value={content.meta?.last_updated || ""} readOnly disabled
            className="w-full bg-[#0D110C]/60 border border-[#283524] text-[#8D9A87] px-3 py-2 font-mono-ui text-xs cursor-not-allowed"
          />
        </div>
      </div>

      {/* Connection */}
      <div className="bg-[#161D14] border border-[#283524] p-5">
        <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Connection</h4>
        <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">
          // IP and port shown on /guide for players to copy into Zomboid.
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField label="Server IP" value={content.server_info?.ip || ""}
            onChange={(v) => updateServer({ ip: v })} testid="guide-server-ip"
            hint="Hostname or IPv4, no port." />
          <TextField label="Port" value={content.server_info?.port || ""}
            onChange={(v) => updateServer({ port: v })} testid="guide-server-port" />
        </div>
      </div>

      {/* Sections */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Sections</h4>
          <button type="button" onClick={addSection} data-testid="guide-add-section"
            className="inline-flex items-center gap-2 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em]">
            <Plus size={12} /> Add section
          </button>
        </div>
        <div className="space-y-4">
          <SortableGuideSections
            sections={content.sections}
            updateSections={updateSections}
            icons={icons}
            colors={colors}
            blockTypes={blockTypes}
            calloutKinds={calloutKinds}
          />
        </div>
      </div>
    </div>
  );
}

// -------- DnD wrapper for sections --------

function DragHandle({ testid, title, attributes, listeners }) {
  return (
    <button
      type="button"
      {...attributes}
      {...listeners}
      data-testid={testid}
      title={title || "Drag to reorder"}
      // Swallow clicks that bubble up after a drag release — otherwise the
      // click after a short drag can trigger unwanted actions on nearby
      // controls (like the collapse toggle) and makes the movement feel
      // twitchy.
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onKeyDown={(e) => {
        // Let dnd-kit's KeyboardSensor own arrow / space; block anything
        // else so accidental key presses don't scroll the page while a
        // drag is armed.
        if (["Enter", " ", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Escape", "Tab"].includes(e.key)) return;
        e.preventDefault();
      }}
      className="w-7 h-7 border border-[#283524] text-[#8D9A87] hover:border-[#C0A875] hover:text-[#C0A875] flex items-center justify-center cursor-grab active:cursor-grabbing touch-none select-none focus:outline-none focus:border-[#C0A875] focus:text-[#C0A875]"
      style={{ touchAction: "none" }}
    >
      <GripVertical size={13} />
    </button>
  );
}

function SortableGuideSections({ sections, updateSections, icons, colors, blockTypes, calloutKinds }) {
  const [activeId, setActiveId] = useState(null);
  // Backfill a stable per-section `_uid` so React keys + DnD ids stay
  // constant even when the slug (`section.id`) is edited. Without this,
  // typing in the Slug field changes the React key → the whole section card
  // unmounts and its `collapsed` useState resets to true, closing the panel
  // mid-keystroke.
  useEffect(() => {
    if (!sections?.length) return;
    if (sections.some((s) => !s._uid)) {
      updateSections((arr) => arr.map((s) => (s._uid ? s : { ...s, _uid: uid() })));
    }
  }, [sections]);
  const sensors = useSensors(
    // Higher activation distance = fewer accidental drags when the admin
    // is just clicking around the row. 8 px is enough to feel like a
    // deliberate drag start without being sluggish.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragCancel = () => setActiveId(null);
  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    updateSections((arr) => {
      const oldIndex = arr.findIndex((s) => s._uid === active.id);
      const newIndex = arr.findIndex((s) => s._uid === over.id);
      if (oldIndex < 0 || newIndex < 0) return arr;
      return arrayMove(arr, oldIndex, newIndex).map((s, i) => ({ ...s, number: String(i + 1).padStart(2, "0") }));
    });
  };
  const activeSection = activeId ? sections.find((s) => s._uid === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      // Re-measure drop targets on every frame. Sections in this list can
      // expand/collapse (the edit panel) mid-drag, which was the biggest
      // source of the "awkward mouse movement" the user reported — without
      // this, dnd-kit uses cached rects that don't match the moved cards.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sections.map((s) => s._uid).filter(Boolean)} strategy={verticalListSortingStrategy}>
        {sections.map((sec, idx) => (
          <SortableSectionItem
            key={sec._uid || sec.id}
            section={sec}
            icons={icons}
            colors={colors}
            blockTypes={blockTypes}
            calloutKinds={calloutKinds}
            onChange={(patch) => updateSections((arr) => arr.map((s, i) => (i === idx ? { ...s, ...patch } : s)))}
            onDelete={() => {
              if (!window.confirm(`Delete section "${sec.title}"?`)) return;
              updateSections((arr) =>
                arr.filter((_, i) => i !== idx).map((s, i) => ({ ...s, number: String(i + 1).padStart(2, "0") }))
              );
            }}
          />
        ))}
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeSection ? <SectionDragPreview section={activeSection} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function SectionDragPreview({ section }) {
  const Icon = GUIDE_ICON_MAP[section.icon] || Book;
  const accent = section.color || "#C0A875";
  return (
    <div className="bg-[#161D14] border shadow-2xl cursor-grabbing rotate-1 opacity-95"
      style={{ borderColor: accent, boxShadow: `0 20px 40px -10px ${accent}66` }}>
      <div className="flex items-center gap-3 p-4">
        <div className="w-7 h-7 border border-[#283524] flex items-center justify-center">
          <GripVertical size={13} className="text-[#C0A875]" />
        </div>
        <div className="w-9 h-9 border flex items-center justify-center shrink-0" style={{ borderColor: accent, color: accent }}>
          <Icon size={16} />
        </div>
        <div className="min-w-0">
          <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">Section {section.number}</div>
          <div className="font-display uppercase text-[#E2E4DF] text-base sm:text-lg truncate">{section.title}</div>
        </div>
      </div>
    </div>
  );
}

function SortableSectionItem({ section, icons, colors, blockTypes, calloutKinds, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section._uid || section.id });
  // `willChange` promotes the row to its own GPU layer so the transform
  // updates during drag paint at 60 fps instead of jittering on the main
  // thread. Hide the source row while dragging (DragOverlay renders the
  // floating preview) — otherwise you see a duplicate ghost trailing the
  // cursor which reads as "buggy movement".
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    willChange: "transform",
    opacity: isDragging ? 0 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <GuideSectionCard
        section={section}
        icons={icons}
        colors={colors}
        blockTypes={blockTypes}
        calloutKinds={calloutKinds}
        onChange={onChange}
        onDelete={onDelete}
        dragHandleProps={{ attributes, listeners }}
        isDragging={isDragging}
      />
    </div>
  );
}

// -------- Section card --------

function GuideSectionCard({ section, icons, colors, blockTypes, calloutKinds, onChange, onDelete, dragHandleProps, isDragging }) {
  const [collapsed, setCollapsed] = useState(true);
  const Icon = GUIDE_ICON_MAP[section.icon] || Book;
  const accent = section.color || "#C0A875";
  const updateSubs = (fn) => onChange({ subsections: fn(section.subsections || []) });

  return (
    <div className={`bg-[#161D14] border ${isDragging ? "opacity-70 ring-2 ring-[#C0A875]" : ""}`}
      style={{ borderColor: "#283524" }}
      data-testid={`guide-section-editor-${section.id}`}>
      <div className="flex items-center gap-3 p-4 border-b border-[#283524]">
        <DragHandle {...dragHandleProps} testid={`guide-section-drag-${section.id}`} title="Drag to reorder" />
        <div className="w-9 h-9 border flex items-center justify-center shrink-0" style={{ borderColor: accent, color: accent }}>
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">Section {section.number}</div>
          <div className="font-display uppercase text-[#E2E4DF] text-base sm:text-lg truncate">{section.title}</div>
        </div>
        <IconBtn onClick={() => setCollapsed((v) => !v)} title={collapsed ? "Expand" : "Collapse"} testid={`guide-section-collapse-${section.id}`}>
          <Pencil size={12} />
        </IconBtn>
        <IconBtn onClick={onDelete} title="Delete" danger testid={`guide-section-delete-${section.id}`}>
          <Trash2 size={12} />
        </IconBtn>
      </div>

      {!collapsed && (
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <TextField label="Number" value={section.number || ""} onChange={(v) => onChange({ number: v })} />
            <div className="sm:col-span-2">
              <TextField label="Title" value={section.title || ""} onChange={(v) => onChange({ title: v })} />
            </div>
            <IconPicker label="Icon" value={section.icon} icons={icons} onChange={(v) => onChange({ icon: v })} />
          </div>
          <TextField label="Slug (URL anchor)" value={section.id || ""}
            onChange={(v) => onChange({ id: v.replace(/[^a-z0-9-]/gi, "-").toLowerCase() })}
            hint="Used for the #anchor. Keep lowercase-with-dashes." />
          <div>
            <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
              Highlight colour <span style={{ color: accent }}>■</span>
            </label>
            <HexColorRow
              value={section.color || "#C0A875"}
              onChange={(c) => onChange({ color: c })}
              palette={colors}
              size="md"
              testidPrefix={`guide-color-${section.id}`}
            />
          </div>

          <div className="border-t border-[#283524] pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">Subsections</span>
              <button type="button" onClick={() => updateSubs((arr) => [...arr, { _uid: uid(), title: "New subsection", blocks: [] }])}
                data-testid={`guide-add-subsection-${section.id}`}
                className="inline-flex items-center gap-1.5 text-[#C0A875] hover:text-[#E2E4DF] font-mono-ui text-[10px] uppercase tracking-[0.25em]">
                <Plus size={11} /> Add subsection
              </button>
            </div>
            <SortableSubsections
              section={section}
              accent={accent}
              colors={colors}
              blockTypes={blockTypes}
              calloutKinds={calloutKinds}
              updateSubs={updateSubs}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Subsection DnD --------

function SortableSubsections({ section, accent, colors, blockTypes, calloutKinds, updateSubs }) {
  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    const subs = section.subsections || [];
    if (subs.length && subs.some((s) => !s._uid)) {
      updateSubs((arr) => arr.map((s) => (s._uid ? s : { ...s, _uid: uid() })));
    }
  }, [section.subsections]);

  const subs = section.subsections || [];
  const items = subs.map((s) => s._uid).filter(Boolean);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragStart = (e) => setActiveId(e.active.id);
  const handleDragCancel = () => setActiveId(null);
  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    updateSubs((arr) => {
      const oldIndex = arr.findIndex((s) => s._uid === active.id);
      const newIndex = arr.findIndex((s) => s._uid === over.id);
      if (oldIndex < 0 || newIndex < 0) return arr;
      return arrayMove(arr, oldIndex, newIndex);
    });
  };
  const activeSub = activeId ? subs.find((s) => s._uid === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="space-y-3">
          {subs.map((sub, si) => (
            <SortableSubsection
              key={sub._uid || si}
              sub={sub}
              si={si}
              sectionId={section.id}
              accent={accent}
              colors={colors}
              blockTypes={blockTypes}
              calloutKinds={calloutKinds}
              updateSubs={updateSubs}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeSub ? <SubDragPreview sub={activeSub} accent={accent} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function SubDragPreview({ sub, accent }) {
  const count = (sub.blocks || []).length;
  return (
    <div className="bg-[#0D110C] border shadow-2xl cursor-grabbing rotate-1 opacity-95 p-3"
      style={{ borderColor: accent, boxShadow: `0 20px 40px -10px ${accent}66` }}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 border border-[#283524] flex items-center justify-center">
          <GripVertical size={13} className="text-[#C0A875]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-ui text-xs truncate" style={{ color: accent }}>{sub.title || "(untitled)"}</div>
          <div className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51] mt-0.5">
            {count} block{count === 1 ? "" : "s"}
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableSubsection({ sub, si, sectionId, accent, colors, blockTypes, calloutKinds, updateSubs }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sub._uid || `fb-${si}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    willChange: "transform",
    opacity: isDragging ? 0 : 1,
  };
  const blocks = sub.blocks || [];

  const updateBlocks = (fn) => updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, blocks: fn(s.blocks || []) } : s)));
  const addBlock = (type) => {
    let block = { type };
    if (type === "paragraph") block = { type, text: "" };
    if (type === "steps") block = { type, items: [""] };
    if (type === "code") block = { type, code: "", lang: "" };
    if (type === "image") block = { type, src: "", alt: "", caption: "" };
    if (type === "callout") block = { type, kind: "info", text: "" };
    if (type === "table") block = { type, headers: ["Column A", "Column B"], rows: [["", ""]] };
    updateBlocks((arr) => [...arr, block]);
  };
  const moveBlock = (idx, dir) => updateBlocks((arr) => {
    const next = [...arr];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return arr;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  return (
    <div ref={setNodeRef} style={style}
      className={`bg-[#0D110C] border border-[#283524] p-3 space-y-3 ${isDragging ? "opacity-70 ring-2 ring-[#C0A875]" : ""}`}
      data-testid={`guide-subsection-${sectionId}-${si}`}>
      <div className="flex items-center gap-2">
        <DragHandle attributes={attributes} listeners={listeners}
          testid={`guide-subsection-drag-${sectionId}-${si}`} title="Drag to reorder" />
        <input value={sub.title || ""}
          onChange={(e) => updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title: e.target.value } : s)))}
          className="flex-1 bg-[#0D110C] border border-[#283524] px-3 py-2 font-mono-ui text-xs focus:outline-none"
          style={{ color: sub.title_color || accent, borderColor: "#283524" }}
          placeholder="Subsection title (optional)" />
        <IconBtn danger onClick={() => updateSubs((arr) => arr.filter((_, i) => i !== si))} title="Delete subsection">
          <Trash2 size={12} />
        </IconBtn>
      </div>

      {/* Title styling — optional per-subsection color override, size + underline */}
      {sub.title !== undefined && (
        <div
          className="flex flex-wrap items-center gap-2 pl-8 py-1.5 border-l border-[#283524]"
          data-testid={`guide-subsection-styles-${sectionId}-${si}`}
        >
          {/* Size picker */}
          <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
            // size:
          </span>
          {[
            { key: "compact", label: "S" },
            { key: "normal", label: "M" },
            { key: "huge", label: "L" },
          ].map((opt) => {
            const active = (sub.title_size || "normal") === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() =>
                  updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title_size: opt.key } : s)))
                }
                title={`Title size: ${opt.key}`}
                data-testid={`guide-subsection-size-${sectionId}-${si}-${opt.key}`}
                className={`h-6 min-w-[24px] px-1.5 border-2 font-mono-ui text-[10px] font-bold ${
                  active
                    ? "border-[#E2E4DF] text-[#E2E4DF] bg-[#283524]/50"
                    : "border-[#283524] text-[#5C6B51] hover:border-[#8D9A87]"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
          <span className="w-px h-4 bg-[#283524] mx-1" />

          {/* Color picker */}
          <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
            // color:
          </span>
          <HexColorRow
            value={sub.title_color}
            onChange={(c) => updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title_color: c } : s)))}
            onClear={() => updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title_color: null } : s)))}
            clearLabel="A"
            clearTitle="Use section accent"
            palette={colors}
            size="sm"
            testidPrefix={`guide-subsection-color-${sectionId}-${si}`}
          />
          <label
            className="ml-3 inline-flex items-center gap-1.5 cursor-pointer select-none"
            title="Underline the title on the public page"
          >
            <input
              type="checkbox"
              checked={!!sub.title_underline}
              onChange={(e) =>
                updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title_underline: e.target.checked } : s)))
              }
              data-testid={`guide-subsection-underline-${sectionId}-${si}`}
              className="accent-[#C0A875]"
            />
            <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#8D9A87]">
              underline
            </span>
          </label>

          {/* Divider above this subsection (skipped for the first). */}
          {si > 0 && (
            <>
              <div className="inline-flex items-center gap-1.5 ml-3">
                <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
                  // divider:
                </span>
                <select
                  value={sub.divider || "line"}
                  onChange={(e) =>
                    updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, divider: e.target.value } : s)))
                  }
                  data-testid={`guide-subsection-divider-${sectionId}-${si}`}
                  className="bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-1.5 py-1 font-mono-ui text-[10px] uppercase focus:border-[#C0A875] focus:outline-none"
                >
                  <option value="line">— Line</option>
                  <option value="dashed">─ ─ Dashed</option>
                  <option value="dotted">· · Dotted</option>
                  <option value="double">= Double</option>
                  <option value="accent">━ Accent</option>
                  <option value="glyph">✦ Glyph</option>
                  <option value="none">✕ None</option>
                </select>
              </div>
              {(sub.divider || "line") !== "none" && (
                <div className="inline-flex items-center gap-1.5 ml-1">
                  <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
                    color:
                  </span>
                  <HexColorRow
                    value={sub.divider_color}
                    onChange={(c) =>
                      updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, divider_color: c } : s)))
                    }
                    onClear={() =>
                      updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, divider_color: null } : s)))
                    }
                    clearLabel="A"
                    clearTitle="Auto (gray / subsection color)"
                    palette={colors}
                    size="sm"
                    testidPrefix={`guide-subsection-divider-color-${sectionId}-${si}`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Blocks */}
      <div className="space-y-3">
        {blocks.map((b, bi) => (
          <BlockEditor
            key={bi}
            block={b}
            calloutKinds={calloutKinds}
            onChange={(patch) => updateBlocks((arr) => arr.map((x, i) => (i === bi ? { ...x, ...patch } : x)))}
            onMoveUp={() => moveBlock(bi, -1)}
            onMoveDown={() => moveBlock(bi, 1)}
            onDelete={() => updateBlocks((arr) => arr.filter((_, i) => i !== bi))}
            first={bi === 0}
            last={bi === blocks.length - 1}
          />
        ))}
        {blocks.length === 0 && (
          <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">
            // No content yet — pick a block below to start.
          </p>
        )}
      </div>

      {/* Add-block bar */}
      <div className="pt-2 border-t border-[#283524] flex flex-wrap items-center gap-2">
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">// Add block:</span>
        {blockTypes.map((t) => (
          <button key={t} type="button" onClick={() => addBlock(t)}
            data-testid={`guide-add-block-${sectionId}-${si}-${t}`}
            className="border border-[#283524] hover:border-[#C0A875] text-[#8D9A87] hover:text-[#C0A875] px-2 py-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors">
            + {t}
          </button>
        ))}
      </div>
    </div>
  );
}

// -------- Block editors --------

function BlockEditor({ block, calloutKinds, onChange, onMoveUp, onMoveDown, onDelete, first, last }) {
  return (
    <div className="border border-[#283524] bg-[#161D14]">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#283524]">
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#C0A875]">
          {block.type}
        </span>
        <div className="flex-1" />
        <IconBtn onClick={onMoveUp} disabled={first} title="Move up"><span className="font-mono-ui text-xs">↑</span></IconBtn>
        <IconBtn onClick={onMoveDown} disabled={last} title="Move down"><span className="font-mono-ui text-xs">↓</span></IconBtn>
        <IconBtn danger onClick={onDelete} title="Delete block"><Trash2 size={12} /></IconBtn>
      </div>
      <div className="p-3 space-y-2">
        {block.type === "paragraph" && (
          <MarkdownTextArea label="Text" rows={4} value={block.text || ""} onChange={(v) => onChange({ text: v })} />
        )}
        {block.type === "steps" && (
          <StepsEditor block={block} onChange={onChange} />
        )}
        {block.type === "code" && (
          <>
            <TextField label="Language (optional)" value={block.lang || ""} onChange={(v) => onChange({ lang: v })} hint="e.g. ini, bash, lua" />
            <TextArea label="Code" rows={5} value={block.code || ""} onChange={(v) => onChange({ code: v })} />
          </>
        )}
        {block.type === "image" && (
          <ImageBlockEditor block={block} onChange={onChange} />
        )}
        {block.type === "callout" && (
          <>
            <div>
              <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">Kind</label>
              <select value={block.kind || "info"} onChange={(e) => onChange({ kind: e.target.value })}
                className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none">
                {calloutKinds.map((k) => (<option key={k} value={k}>{k}</option>))}
              </select>
            </div>
            <MarkdownTextArea label="Text" rows={3} value={block.text || ""} onChange={(v) => onChange({ text: v })} />
          </>
        )}
        {block.type === "table" && (
          <TableEditor block={block} onChange={onChange} />
        )}
      </div>
    </div>
  );
}

function ImageBlockEditor({ block, onChange }) {
  const [uploading, setUploading] = useState(false);

  // The stored `src` can be either a full URL (Discord CDN, Imgur, etc.) or a
  // relative /api/uploads/... path when it was uploaded here. For preview we
  // pass it through unchanged — both work in <img src>.
  const previewSrc = block.src || null;

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked later
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error("Not an image file.");
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error(`Image is ${(f.size / 1024 / 1024).toFixed(1)} MB — max is 6 MB.`);
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const res = await api.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ src: res.data.url });
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Upload / paste-URL side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
        <TextField label="Image URL or upload →" value={block.src || ""} onChange={(v) => onChange({ src: v })} />
        <label className={`inline-flex items-center gap-2 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] cursor-pointer transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
          {uploading ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />}
          {uploading ? "Uploading…" : "Upload"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={handleFile}
            className="hidden"
            data-testid="guide-image-upload-input"
          />
        </label>
      </div>
      {previewSrc && (
        <div className="border border-[#283524] bg-[#0D110C] p-2">
          <div className="label-tag text-[#5C6B51] mb-1">// PREVIEW</div>
          <img
            src={previewSrc}
            alt={block.alt || ""}
            className="max-h-48 border border-[#283524] object-contain bg-black/20"
          />
        </div>
      )}
      <TextField label="Alt text" value={block.alt || ""} onChange={(v) => onChange({ alt: v })} />
      <TextField label="Caption (optional)" value={block.caption || ""} onChange={(v) => onChange({ caption: v })} />
    </div>
  );
}

function StepsEditor({ block, onChange }) {
  const items = block.items || [];
  const set = (i, v) => onChange({ items: items.map((x, j) => (j === i ? v : x)) });
  const add = () => onChange({ items: [...items, ""] });
  const del = (i) => onChange({ items: items.filter((_, j) => j !== i) });
  const move = (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ items: next });
  };
  return (
    <div>
      <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">Steps</label>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="w-6 h-6 border border-[#283524] text-[#C0A875] font-mono-ui text-[10px] flex items-center justify-center shrink-0">
              {String(i + 1).padStart(2, "0")}
            </span>
            <textarea rows={1} value={it} onChange={(e) => set(i, e.target.value)}
              className="flex-1 bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
              placeholder="Step description…" />
            <IconBtn onClick={() => move(i, -1)} disabled={i === 0} title="Move up"><span className="font-mono-ui text-xs">↑</span></IconBtn>
            <IconBtn onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Move down"><span className="font-mono-ui text-xs">↓</span></IconBtn>
            <IconBtn danger onClick={() => del(i)} title="Delete"><XCircle size={12} /></IconBtn>
          </div>
        ))}
        <button type="button" onClick={add}
          className="mt-1 ml-8 text-[#8D9A87] hover:text-[#C0A875] font-mono-ui text-[10px] uppercase tracking-[0.25em]">
          + Add step
        </button>
      </div>
    </div>
  );
}

function TableEditor({ block, onChange }) {
  const headers = block.headers || [];
  const rows = block.rows || [];
  const setHeader = (i, v) => onChange({ headers: headers.map((x, j) => (j === i ? v : x)) });
  const addColumn = () => onChange({
    headers: [...headers, "Column"],
    rows: rows.map((r) => [...r, ""]),
  });
  const delColumn = (i) => onChange({
    headers: headers.filter((_, j) => j !== i),
    rows: rows.map((r) => r.filter((_, j) => j !== i)),
  });
  const addRow = () => onChange({ rows: [...rows, headers.map(() => "")] });
  const delRow = (i) => onChange({ rows: rows.filter((_, j) => j !== i) });
  const setCell = (ri, ci, v) => onChange({
    rows: rows.map((r, rj) => (rj === ri ? r.map((c, cj) => (cj === ci ? v : c)) : r)),
  });
  return (
    <div>
      <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">Table</label>
      <div className="overflow-x-auto border border-[#283524]">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-[#0D110C] border-b border-[#283524]">
              {headers.map((h, i) => (
                <th key={i} className="p-1.5">
                  <div className="flex items-center gap-1">
                    <input value={h} onChange={(e) => setHeader(i, e.target.value)}
                      className="w-full bg-[#161D14] border border-[#283524] text-[#C0A875] px-2 py-1 font-mono-ui text-[10px] uppercase tracking-[0.15em] focus:outline-none" />
                    <IconBtn danger onClick={() => delColumn(i)} title="Delete column"><XCircle size={10} /></IconBtn>
                  </div>
                </th>
              ))}
              <th className="p-1.5 w-16">
                <button type="button" onClick={addColumn}
                  className="w-full border border-[#283524] hover:border-[#C0A875] text-[#8D9A87] hover:text-[#C0A875] px-2 py-1 font-mono-ui text-[10px] uppercase tracking-[0.25em]">
                  + Col
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="border-b border-[#283524] last:border-b-0">
                {row.map((c, ci) => (
                  <td key={ci} className="p-1.5">
                    <input value={c} onChange={(e) => setCell(ri, ci, e.target.value)}
                      className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-2 py-1 font-mono-ui text-[11px] focus:border-[#C0A875] focus:outline-none" />
                  </td>
                ))}
                <td className="p-1.5">
                  <IconBtn danger onClick={() => delRow(ri)} title="Delete row"><XCircle size={12} /></IconBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={addRow}
        className="mt-2 border border-[#283524] hover:border-[#C0A875] text-[#8D9A87] hover:text-[#C0A875] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.25em]">
        + Add row
      </button>
    </div>
  );
}
